/**
 * L'orbite — pourquoi le carré de la distance ?
 *
 * Troisième expérience, et celle que SPEC §9 attendait : c'est elle qui rend
 * l'intégrateur **nécessaire**. Sous pesanteur constante, la chute libre a une
 * solution polynomiale que velocity Verlet reproduit exactement ; le noyau y
 * était exercé, pas indispensable. Ici la pesanteur varie avec la distance, et
 * aucune forme close élémentaire ne donne la position en fonction du temps.
 *
 * Elle mesure aussi ce que valait la généralisation de la page. Ce fichier ne
 * contient aucune ligne de rendu : des réglages, trois fonctions de l'état
 * vers ce qui s'affiche, et une vue montée depuis un module qui ne connaît ni
 * étoile ni exposant.
 */

import contentJson from "../content/orbit.json?raw";
import type { OrbitModel, OrbitPoint, OrbitSetup } from "../core/orbit.js";
import type { Quantity } from "../core/units.js";
import type {
  ChartData,
  ComparisonSection,
  Experiment,
  Preset,
  ReadoutRow,
  Series,
} from "./types.js";
import type { OrbitSceneRequest } from "../view/orbit-scene.js";
import {
  NEWTON_ORBIT,
  TUNED_ORBIT,
  circularSpeed,
  keplerPeriod,
  orbitPath,
  precessionOfPath,
} from "../core/orbit.js";
import { dimensionless, quantity, valueIn } from "../core/units.js";
import { loadExperimentContent } from "../content/schema.js";
import { control } from "./types.js";
import { freeHypothesesLabel } from "../view/readout.js";
import { formatNumber } from "../view/format.js";
import { mountOrbitScene, renderOrbitShell } from "../view/orbit-view.js";
import { fr } from "../i18n/fr.js";

// ── L'état réglable ──────────────────────────────────────────────────────────

export interface OrbitExploration {
  readonly centralMass: Quantity<"mass">;
  readonly distance: Quantity<"length">;
  /** Vitesse de départ en fraction de la vitesse circulaire. */
  readonly speedFraction: Quantity<"dimensionless">;
  /** L'hypothèse libre : l'exposant de décroissance. */
  readonly exponent: Quantity<"dimensionless">;
}

/**
 * Une étoile d'ordre solaire, une orbite d'ordre terrestre, et une ellipse
 * franche : au départ, la précession doit déjà se voir.
 */
export const DEFAULT_ORBIT: OrbitExploration = {
  centralMass: quantity(1.989e30, "kg"),
  distance: quantity(1.496e11, "m"),
  speedFraction: dimensionless(0.82),
  exponent: dimensionless(2.3),
};

const MODELS: readonly OrbitModel[] = [NEWTON_ORBIT, TUNED_ORBIT];

/** Nombre de tours simulés. Assez pour qu'une rosace se voie comme une rosace. */
const TURNS = 6;
/** Points par tour. Assez pour que le sillage soit lisse, pas plus. */
const SAMPLES_PER_TURN = 320;

function setupOf(exploration: OrbitExploration): OrbitSetup {
  const circular = valueIn(
    circularSpeed(exploration.centralMass, exploration.distance),
    "m/s",
  );
  return {
    centralMass: exploration.centralMass,
    distance: exploration.distance,
    speed: quantity(circular * valueIn(exploration.speedFraction, "1"), "m/s"),
    exponent: exploration.exponent,
  };
}

/** La durée simulée : quelques tours de l'orbite circulaire de référence. */
function span(exploration: OrbitExploration): Quantity<"time"> {
  const period = valueIn(keplerPeriod(exploration.centralMass, exploration.distance), "s");
  return quantity(TURNS * period, "s");
}

const SAMPLES = dimensionless(TURNS * SAMPLES_PER_TURN);

/**
 * Le chemin, calculé une fois par jeu de réglages.
 *
 * Sans cette mémoire, la vue réintégrerait quinze mille pas par modèle et par
 * image — deux millions de pas par seconde pour dessiner un mouvement qui ne
 * change pas. Ce n'est pas une optimisation de confort : la vue serait
 * inutilisable.
 *
 * La mémoire ne rend pas la fonction moins pure. Elle est indexée par les
 * réglages, et deux appels avec les mêmes réglages rendaient déjà le même
 * chemin — c'est l'absence d'aléa et d'horloge dans le noyau qui l'autorise
 * (CLAUDE.md §5).
 */
interface CachedOrbit {
  readonly path: readonly OrbitPoint[];
  readonly scenePath: readonly { readonly x: number; readonly y: number }[];
  readonly precession: Quantity<"angle"> | undefined;
}
const cache = new Map<string, CachedOrbit>();
let cacheKey = "";

/**
 * Le jeu de réglages, réduit à une clé.
 *
 * **Exportée pour être tenue honnête.** Elle doit énumérer *tous* les réglages
 * de `OrbitExploration` : un réglage ajouté à l'état sans l'être ici rendrait
 * une trajectoire périmée — le curseur bouge, le chiffre change, la courbe ne
 * change pas. Ni erreur, ni avertissement, exactement la classe de défaut que
 * CLAUDE.md §8 décrit pour `setFromPoints`.
 *
 * `tests/orbit-experiment.test.ts` s'en assure en bouclant sur `ORBIT_CONTROLS`
 * plutôt qu'en recopiant la liste : un cinquième curseur rend le test rouge de
 * lui-même.
 */
export function signatureOf(exploration: OrbitExploration): string {
  return [
    valueIn(exploration.centralMass, "kg"),
    valueIn(exploration.distance, "m"),
    valueIn(exploration.speedFraction, "1"),
    valueIn(exploration.exponent, "1"),
  ].join("|");
}

function resultOf(model: OrbitModel, exploration: OrbitExploration): CachedOrbit {
  const key = signatureOf(exploration);
  if (key !== cacheKey) {
    cache.clear();
    cacheKey = key;
  }

  const known = cache.get(model.id);
  if (known !== undefined) return known;

  const computed = orbitPath(model, setupOf(exploration), span(exploration), SAMPLES);
  const result: CachedOrbit = {
    path: computed,
    scenePath: computed.map((point) => ({ x: valueIn(point.position.x, "m"), y: valueIn(point.position.y, "m") })),
    precession: precessionOfPath(computed),
  };
  cache.set(model.id, result);
  return result;
}

// ── Les réglages ─────────────────────────────────────────────────────────────

/** Une unité astronomique, pour donner au curseur de distance une échelle lisible. */
const AU = 1.496e11;
/** Une masse solaire. */
const SOLAR_MASS = 1.989e30;

export const ORBIT_CONTROLS = [
  control<OrbitExploration, "mass">({
    id: "centralMass",
    label: fr.controlCentralMass,
    hint: fr.controlCentralMassHint,
    unit: "kg",
    min: 0.2 * SOLAR_MASS,
    max: 5 * SOLAR_MASS,
    step: 0.01 * SOLAR_MASS,
    decimals: 0,
    freeHypothesis: false,
    read: (e) => e.centralMass,
    write: (e, value) => ({ ...e, centralMass: value }),
    readout: (e) =>
      `${formatNumber(valueIn(e.centralMass, "kg") / SOLAR_MASS, 2)} M☉`,
  }),
  control<OrbitExploration, "length">({
    id: "orbitDistance",
    label: fr.controlOrbitDistance,
    hint: fr.controlOrbitDistanceHint,
    unit: "m",
    min: 0.3 * AU,
    max: 3 * AU,
    step: 0.01 * AU,
    decimals: 0,
    freeHypothesis: false,
    read: (e) => e.distance,
    write: (e, value) => ({ ...e, distance: value }),
    readout: (e) => `${formatNumber(valueIn(e.distance, "m") / AU, 2)} ua`,
  }),
  control<OrbitExploration, "dimensionless">({
    id: "speedFraction",
    label: fr.controlOrbitSpeed,
    hint: fr.controlOrbitSpeedHint,
    unit: "1",
    min: 0.55,
    max: 1,
    step: 0.01,
    decimals: 2,
    freeHypothesis: false,
    read: (e) => e.speedFraction,
    write: (e, value) => ({ ...e, speedFraction: value }),
  }),
  control<OrbitExploration, "dimensionless">({
    id: "exponent",
    label: fr.controlExponent,
    hint: fr.controlExponentHint,
    unit: "1",
    // Au-delà de trois, l'orbite n'est plus liée et le corps part : la borne
    // est celle du régime où la question posée a encore un sens.
    min: 1.5,
    max: 2.9,
    step: 0.01,
    decimals: 2,
    freeHypothesis: true,
    read: (e) => e.exponent,
    write: (e, value) => ({ ...e, exponent: value }),
  }),
];

/**
 * Trois configurations qui disent chacune quelque chose.
 *
 * Ce ne sont pas des « jolis réglages » : la première montre que la loi libre
 * **contient** Newton, la deuxième que le cercle ne peut rien montrer faute de
 * grand axe, la troisième que la rosace n'a rien de subtil dès qu'on s'écarte.
 */
const PRESETS: readonly Preset<OrbitExploration>[] = [
  {
    id: "newton",
    label: fr.presetNewtonExponent,
    apply: (e) => ({ ...e, exponent: dimensionless(2) }),
  },
  {
    id: "circle",
    label: fr.presetCircularOrbit,
    apply: (e) => ({ ...e, speedFraction: dimensionless(1) }),
  },
  {
    id: "rosace",
    label: fr.presetRosette,
    apply: (e) => ({ ...e, exponent: dimensionless(2.5), speedFraction: dimensionless(0.8) }),
  },
];

// ── Ce qui se trace ──────────────────────────────────────────────────────────

/**
 * La distance à l'étoile au fil du temps, en unités de la distance de départ.
 *
 * C'est le rendu plat de ce que la scène montre en volume, et il porte la même
 * information : chez Newton les creux reviennent identiques, avec l'exposant
 * libre ils dérivent. La règle §3 l'exige — ce qui se lit dans une figure doit
 * se lire ailleurs qu'en la regardant.
 */
export function orbitChart(exploration: OrbitExploration): ChartData {
  const reference = valueIn(exploration.distance, "m");
  const seconds = valueIn(span(exploration), "s");

  const series: readonly Series[] = MODELS.map((model) => ({
    id: model.id,
    label: model.label,
    points: resultOf(model, exploration).path.map((point) => ({
      x: valueIn(point.time, "s") / seconds,
      y: valueIn(point.radius, "m") / reference,
    })),
  }));

  return {
    title: fr.orbitChartTitle,
    description: fr.orbitChartDescription(
      formatNumber(valueIn(exploration.exponent, "1"), 2),
    ),
    caveat: fr.predictionsOnlyCaveat,
    yAxisLabel: fr.axisOrbitRadius,
    formatX: (fraction) => `${formatNumber(fraction * TURNS, 1)} T`,
    series,
    emptyTitle: fr.noOrbitTitle,
    emptyExplanation: fr.noOrbitExplanation,
  };
}

// ── Ce qui se chiffre ────────────────────────────────────────────────────────

function readoutRow(model: OrbitModel, exploration: OrbitExploration): ReadoutRow {
  const free = freeHypothesesLabel(model.freeHypotheses);
  const period = valueIn(keplerPeriod(exploration.centralMass, exploration.distance), "s");

  const precession = resultOf(model, exploration).precession;

  const precessionCell =
    precession === undefined
      ? fr.orbitNotMeasured
      : Math.abs(valueIn(precession, "deg")) < 0.5
        ? fr.orbitClosed
        : `${formatNumber(valueIn(precession, "deg"), 1)} °`;

  return {
    header: model.label,
    cells: [`${formatNumber(period / (365.25 * 86400), 2)} an`, precessionCell, free],
  };
}

// ── Ce qui se confronte : rien, et il faut le dire ───────────────────────────

function orbitComparison(): ComparisonSection {
  return { kind: "unavailable", reasons: [fr.noDatasetForExperiment] };
}

// ── La vue ───────────────────────────────────────────────────────────────────

/**
 * Durée de lecture, en secondes d'écran.
 *
 * Une orbite terrestre dure un an : la jouer en temps réel n'aurait aucun
 * sens. On choisit donc une durée d'affichage, et le curseur de temps est
 * gradué dessus. Ce n'est pas une entorse au principe — l'instant physique
 * reste une entrée explicite, seule sa cadence d'affichage est conventionnelle.
 */
const PLAYBACK_SECONDS = 18;

function orbitSceneAt(exploration: OrbitExploration, elapsed: number): OrbitSceneRequest {
  // La progression se rapporte à la durée de **lecture**, pas à la durée
  // physique : une orbite terrestre dure un an, et le curseur est gradué sur
  // les secondes d'écran.
  const progress = elapsed / PLAYBACK_SECONDS;

  return {
    scale: valueIn(exploration.distance, "m"),
    bodies: MODELS.map((model) => ({
      id: model.id,
      label: model.label,
      path: resultOf(model, exploration).scenePath,
      progress,
    })),
  };
}

// ── L'expérience ─────────────────────────────────────────────────────────────

export const ORBIT: Experiment<OrbitExploration> = {
  id: "orbite",
  label: fr.orbitLabel,
  question: fr.orbitQuestion,
  lede: fr.orbitLede,
  content: loadExperimentContent(contentJson),

  initial: DEFAULT_ORBIT,
  controls: ORBIT_CONTROLS,
  presets: PRESETS,
  presetsLabel: fr.orbitPresetsLabel,

  situationTitle: fr.sectionOrbitSituation,
  situationCaveat: fr.orbitSituationCaveat,
  hypothesisTitle: fr.sectionExponentControl,
  hypothesisCaveat: fr.exponentControlCaveat,

  chart: orbitChart,
  readout: (exploration) => ({
    caption: fr.orbitReadoutCaption,
    columns: [fr.columnModel, fr.columnPeriod, fr.columnPrecession, fr.columnFreeHypotheses],
    rows: MODELS.map((model) => readoutRow(model, exploration)),
  }),
  comparison: orbitComparison,
  scene: {
    title: fr.sectionOrbitScene,
    shell: () =>
      renderOrbitShell(MODELS.map((model) => ({ id: model.id, label: model.label }))),
    mount: (root, current) =>
      mountOrbitScene(root, {
        duration: () => PLAYBACK_SECONDS,
        at: (elapsed) => orbitSceneAt(current(), elapsed),
      }),
  },
};
